import React, { useEffect, useState } from "react";
import { Input } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import { updateProfile } from "../../../redux/slice/rfpModuleSlice/dashboard";
import Button from "style-guide/component/Button";
import Typography from "style-guide/component/Typography";
import { openNotificationWithIcon } from "utils/utils";

export interface IValues {
  firstName: string;
  lastName: string;
  email: string;
  title: string;
}

interface IProfileProps {
  data: IValues;
}

const INITIAL_VALUES: IValues = {
  firstName: "",
  lastName: "",
  email: "",
  title: "",
};

const ProfileDetails: React.FC<IProfileProps> = ({ data }) => {
  const [form, setForm] = useState<IValues>(INITIAL_VALUES);
  const [initialForm, setInitialForm] = useState<IValues>(INITIAL_VALUES);
  const [isDirty, setIsDirty] = useState(false);

  const { isLoadingModal } = useSelector(
    (state: RootState) => state.applicationData
  );
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (data) {
      setForm(data);
      setInitialForm(data);
    }
  }, [data]);

  const validateForm = (form: IValues) => {
    return form.firstName?.trim() !== "" && form.lastName?.trim() !== "" && form.title?.trim() !== "";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const trimmedValue = value.replace(/^\s+/, "");
    const updatedForm = { ...form, [name]: trimmedValue };
    setForm(updatedForm);
    const dirty =
      validateForm(updatedForm) &&
      (updatedForm.firstName?.trim() !== initialForm.firstName?.trim() ||
        updatedForm.lastName?.trim() !== initialForm.lastName?.trim() ||
        updatedForm.title?.trim() !== initialForm.title?.trim());
    setIsDirty(dirty);
  };

  const handleSave = async () => {
    if (!validateForm(form)) return;

    const trimmedForm = {
      ...form,
      firstName: form.firstName?.trim(),
      lastName: form.lastName?.trim(),
      title: form.title?.trim(),
    };

    const changed =
      trimmedForm.firstName !== initialForm.firstName?.trim() ||
      trimmedForm.lastName !== initialForm.lastName?.trim() ||
      trimmedForm.title !== initialForm.title?.trim();

    if (!changed) {
      setIsDirty(false);
      return;
    }

    try {
      const status = await dispatch(updateProfile(trimmedForm));
      if (status === 200) {
        openNotificationWithIcon("success", "Changes have been saved.");
        setInitialForm(trimmedForm);
        setForm(trimmedForm);
        setIsDirty(false);
      }
    } catch (error: unknown) {
      console.error(error);
    }
  };

  return (
    <div className="flex gap-4 py-6">
      <Typography className="w-64" variant="Heading 3" text="Profile Details" />
      <div className="w-[450px] flex flex-col gap-4">
      <div className="flex flex-col">
          <label htmlFor="title">Title</label>
          <Input
            id="title"
            name="title"
            placeholder="Title"
            type="text"
            value={form.title}
            onChange={handleChange}
            disabled={isLoadingModal}
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor="firstName">First Name</label>
          <Input
            id="firstName"
            name="firstName"
            placeholder="First Name"
            type="text"
            value={form.firstName}
            onChange={handleChange}
            disabled={isLoadingModal}
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor="lastName">Last Name</label>
          <Input
            id="lastName"
            name="lastName"
            placeholder="Last Name"
            type="text"
            value={form.lastName}
            onChange={handleChange}
            disabled={isLoadingModal}
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor="email">Email</label>
          <Input
            id="email"
            name="email"
            placeholder="Email"
            type="email"
            value={form.email}
            disabled
            className="!cursor-default"
          />
        </div>
        <Button
          className="w-fit"
          size="Medium"
          buttonText="Save Changes"
          onClick={isDirty ? handleSave : undefined}
          type={isDirty && !isLoadingModal ? "Default" : "Disabled"}
        />
      </div>
    </div>
  );
};

export default ProfileDetails;
